// Human labels for engine states + shared dashboard formatting.

import type { CaseStatus } from '../types/case';

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  INTAKE: 'Intake',
  ASSESSING: 'Assessing',
  GATHERING: 'Gathering info',
  EVALUATING: 'Evaluating',
  NEGOTIATING: 'In negotiation',
  ESCALATING: 'Escalating',
  MEDIATING: 'In mediation',
  RESOLVED: 'Resolved',
  STALLED: 'Stalled',
  CLOSED: 'Closed',
};

export const OPEN_STATUSES: CaseStatus[] = [
  'INTAKE',
  'ASSESSING',
  'GATHERING',
  'EVALUATING',
  'NEGOTIATING',
  'ESCALATING',
  'MEDIATING',
  'STALLED',
];

export const caseStatusLabel = (status: string): string =>
  CASE_STATUS_LABELS[status as CaseStatus] ?? status;

export const DOC_TYPE_LABELS: Record<string, string> = {
  stipulation: 'Stipulation',
  rom: 'Report of Mediation',
  certificate_of_attempts: 'Certificate of Attempts',
  resolution_agreement: 'Resolution Agreement',
};

export const docTypeLabel = (docType: string): string =>
  DOC_TYPE_LABELS[docType] ?? docType;

export const caseRef = (id: string): string => `#${id.slice(0, 8)}`;

/** Best available human title for a case row. */
/** Queue-row label parts: partner (source org), case number exactly as
    entered, and a case name — "Roe v. Wade" party style for courts,
    property address for PM firms (Danny, 2026-08-18). */
export function caseQueueLabel(c: {
  metadata: Record<string, unknown>;
  claims: Array<{ type: string }>;
  org_name?: string | null;
  org_source_type?: string | null;
  property_name?: string | null;
  parties?: Array<{ name: string; role_archetype: string }>;
}): { partner: string | null; caseNo: string | null; name: string } {
  const meta = c.metadata as { court_case_no?: string; property_name?: string; case_name?: string };
  const caseNo = meta.court_case_no ?? null;
  // An explicit case name (mediator-set or migrated) beats the derived
  // party caption — party submissions are often truncated/wrong (Mo,
  // 2026-08-25).
  if (meta.case_name) return { partner: c.org_name ?? null, caseNo, name: meta.case_name };
  const claimant = c.parties?.find((p) => p.role_archetype === 'claimant' && !/^TBD\b/i.test(p.name));
  const respondent = c.parties?.find((p) => p.role_archetype === 'respondent' && !/^TBD\b/i.test(p.name));
  const partyStyle =
    claimant && respondent
      ? `${claimant.name} v. ${respondent.name}`
      : (claimant ?? respondent)?.name ?? null;
  const property = (c.property_name ?? meta.property_name) as string | undefined;
  // Courts read like a caption ("Roe v. Wade"); PM firms read like a
  // rent roll (property address).
  const name =
    (c.org_source_type === 'court' ? partyStyle ?? property : property ?? partyStyle) ??
    caseTitle(c.metadata, c.claims);
  return { partner: c.org_name ?? null, caseNo, name };
}

/** Party line label: domain role when meaningful ("Tenant",
    "Manufacturer Representative"), archetype fallback — courts read
    Plaintiff/Defendant. */
export function partyRoleLabel(
  p: { role?: string | null; role_archetype: string },
  sourceType?: string | null,
): string {
  const role = (p.role ?? '').trim();
  if (role && role !== 'other') {
    return role.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
  if (sourceType === 'court') return p.role_archetype === 'respondent' ? 'Defendant' : 'Plaintiff';
  return p.role_archetype === 'respondent' ? 'Respondent' : 'Claimant';
}

/** Which side of the case a party sits on, for display grouping.
    Principals carry it in role_archetype; associates (representatives,
    participants, intake "other" people) are matched by keywords in their
    role/relationship text. Unmatched associates group under 'other'. */
export function partySideOf(p: {
  role?: string | null;
  role_archetype: string;
  metadata?: Record<string, unknown> | null;
}): 'plaintiff' | 'defendant' | 'other' {
  if (p.role_archetype === 'claimant') return 'plaintiff';
  if (p.role_archetype === 'respondent') return 'defendant';
  const hint = [
    p.role ?? '',
    (p.metadata?.relationship as string) ?? '',
    (p.metadata?.side as string) ?? '',
  ].join(' ').toLowerCase();
  if (/plaintiff|claimant/.test(hint)) return 'plaintiff';
  if (/defendant|respondent/.test(hint)) return 'defendant';
  return 'other';
}

/** Case-detail Parties order (Danny, 2026-08-28): plaintiff always above
    defendant, and each side's associated people grouped directly under
    their principal(s). Stable within groups. */
export function orderPartiesForDisplay<T extends {
  role?: string | null;
  role_archetype: string;
  metadata?: Record<string, unknown> | null;
}>(parties: T[]): T[] {
  const groupIndex = (p: T): number => {
    const side = partySideOf(p);
    const principal = ['claimant', 'respondent'].includes(p.role_archetype);
    if (side === 'plaintiff') return principal ? 0 : 1;
    if (side === 'defendant') return principal ? 2 : 3;
    return 4;
  };
  return parties
    .map((p, i) => ({ p, i }))
    .sort((a, b) => groupIndex(a.p) - groupIndex(b.p) || a.i - b.i)
    .map(({ p }) => p);
}

export function caseTitle(meta: Record<string, unknown>, claims: Array<{ type: string }>): string {
  const m = meta as {
    property_name?: string;
    court_case_no?: string;
    case_name?: string;
    title?: string;
  };
  if (m.case_name) return m.case_name;
  if (m.title) return m.title;
  if (m.court_case_no) return `Court case ${m.court_case_no}`;
  if (m.property_name) return m.property_name;
  const type = claims[0]?.type;
  return type ? type.replace(/_/g, ' ') : 'Case';
}

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** Whole days from today until the given YYYY-MM-DD date (negative = past). */
export function daysUntil(dateIso: string): number {
  const target = new Date(`${dateIso}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
